import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import type { ReactNode } from "react";
import { CanvasToolbarButton } from "./CanvasToolbarButton";
import { canvasToolbarDropdownContentClassName } from "./canvasToolbarStyles";

interface CanvasToolbarDropdownProps {
  label: string;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  /** Icon rendered inside the trigger button. */
  icon: ReactNode;
  /** Show chevron on trigger — Insert uses true; More uses false. */
  showCaret?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  minWidth?: number;
  contentClassName?: string;
  /** Called when the dropdown opens — close bespoke Type-1 submenus. */
  onOpen?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The trigger's rung: the primary bar's 40px tile or the 32px control. */
  size?: "sm" | "tile";
  /** The primary bar's tool key, stamped on the trigger as `data-tool`. */
  tool?: string;
  children: ReactNode;
}

/**
 * Radix dropdown trigger styled as a canvas toolbar button (Type-2 menu).
 */
export function CanvasToolbarDropdown({
  label,
  tooltipSide = "top",
  icon,
  showCaret = true,
  side = "right",
  align = "start",
  sideOffset,
  minWidth = 240,
  contentClassName,
  onOpen,
  open,
  onOpenChange,
  size = "sm",
  tool,
  children,
}: CanvasToolbarDropdownProps) {
  return (
    // Non-modal on purpose: a modal Radix menu puts `pointer-events: none` on
    // <body> while open, and native HTML5 drags respect it — dragging a widget
    // chip out of the Insert menu never delivered dragover/drop to the board.
    // The anchored insert-menu variant is already modal={false} for the same
    // reason (and it keeps menu+dialog combos from deadlocking the body lock).
    <DropdownMenu
      open={open}
      modal={false}
      onOpenChange={(next) => {
        if (next) {
          onOpen?.();
        }
        onOpenChange?.(next);
      }}
    >
      {/* 400ms show delay — the design-system toolbar tooltip cadence; a
          tile's tooltip stands 16 off to clear the bar's shadow, a control's
          12. */}
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <CanvasToolbarButton
              label={undefined}
              aria-label={label}
              dropdown={showCaret}
              size={size}
              data-tool={tool}
            >
              {icon}
            </CanvasToolbarButton>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent
          side={tooltipSide}
          sideOffset={size === "tile" ? 16 : 12}
        >
          {label}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn(canvasToolbarDropdownContentClassName, contentClassName)}
        style={{ minWidth }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
